import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import type { ChoreographerFactory } from '@atlassian/ipm-choreographer';

import { usePostOfficeContext, usePostOfficeEnvironment } from '../index';
import { createInBrowserDebugLogger } from '../util/in-browser-debug-logger';

import type { ChoreographerRegistry } from './choreographerRegistry';
import {
	installChoreographerDevtoolsBridge,
	shouldBypassChoreographerFromDevtools,
} from './devtoolsBridge';
import type { ChoreographerProviderProps } from './types';

// @twood: NEVER CHANGE THIS DEFAULT VALUE
const DEFAULT_IS_CHOREOGRAPHER_ENABLED = true;

const choreographerLogger = createInBrowserDebugLogger('Choreographer');

const defaultContext = {
	choreographerEnabled: DEFAULT_IS_CHOREOGRAPHER_ENABLED,
	setChoreographerEnabled: () => {},
	factory: undefined,
};

export type ChoreographerContext = {
	choreographerEnabled: boolean;
	setChoreographerEnabled: (isEnabled: boolean) => void;
	factory: ChoreographerFactory | undefined;
};

const ChoreographerContext = createContext<ChoreographerContext>(defaultContext);

// Helper to automatically handle ESM and non-ESM imports (either is possible depending on the service/product's build configuration)
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access */
const importHelper = (mod: any): typeof ChoreographerRegistry => {
	if (typeof mod.ChoreographerRegistry === 'function') {
		choreographerLogger.debug('Using CommonJS module format', { moduleType: 'CommonJS' });
		return mod.ChoreographerRegistry as typeof ChoreographerRegistry;
	} else if (typeof mod.default === 'function') {
		choreographerLogger.debug('Using ESM module format (browser)', {
			moduleType: 'ESM-browser',
		});
		return mod.default as typeof ChoreographerRegistry;
	} else if (
		typeof mod?.default === 'object' &&
		typeof mod.default.ChoreographerRegistry === 'function'
	) {
		choreographerLogger.debug('Using ESM module format (Node.js)', {
			moduleType: 'ESM-node',
		});
		return mod.default.ChoreographerRegistry as typeof ChoreographerRegistry;
	}
	choreographerLogger.warn('Using fallback module format', { moduleType: 'fallback' });
	return mod as typeof ChoreographerRegistry;
};
/* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access */

export const StableChoreographerProvider = ({
	children,
	choreographer,
	placementId,
}: {
	children: React.ReactNode;
	choreographer?: ChoreographerProviderProps['choreographer'];
	placementId?: ChoreographerProviderProps['placementId'];
}): JSX.Element => {
	const [choreographerFactory, setChoreographerFactory] = useState<
		ChoreographerFactory | undefined
	>(undefined);
	const [choreographerEnabled, setChoreographerEnabled] = useState(
		choreographer?.isDefaultEnabled ?? defaultContext.choreographerEnabled,
	);
	const environmentContext = usePostOfficeEnvironment();
	const context = usePostOfficeContext();

	useEffect(() => {
		if (shouldBypassChoreographerFromDevtools()) {
			setChoreographerEnabled(false);
		}
	}, [setChoreographerEnabled]);

	useEffect(() => {
		if (choreographerEnabled) {
			// eslint-disable-next-line import/dynamic-import-chunkname
			import(/* webpackChunkName: "post-office-choreographer-registry" */ './choreographerRegistry')
				.then((m) => {
					const ChoreographerRegistryClass = importHelper(m);

					setChoreographerFactory(
						new ChoreographerRegistryClass(
							context.product,
							environmentContext.currentEnv,
						).getPostOfficeChoreographerFactory(),
					);

					choreographerLogger.info('ChoreographerFactory created', {
						choreographerEnabled,
						product: context.product,
						placementId,
					});
				})
				.catch((error: unknown) => {
					choreographerLogger.error('ChoreographerRegistry import failed', {
						error: error instanceof Error ? error.message : String(error),
						stack: error instanceof Error ? error.stack : undefined,
						product: context.product,
						placementId,
					});

					choreographerLogger.warn('Disabling Choreographer due to import failure');
					setChoreographerEnabled(false);
				});
		} else {
			choreographerLogger.debug('Choreographer disabled, skipping import', {
				product: context.product,
				placementId,
			});
		}
	}, [choreographerEnabled, context.product, environmentContext.currentEnv, placementId]);

	const stableChoreographerContext = useMemo(() => {
		return {
			factory: choreographerFactory,
			choreographerEnabled,
			setChoreographerEnabled,
		};
	}, [choreographerFactory, choreographerEnabled, setChoreographerEnabled]);

	useEffect(() => {
		choreographerLogger.debug('Setting ChoreographerContext', {
			factory: choreographerFactory,
			choreographerEnabled,
			product: context.product,
			placementId,
		});
	}, [context.product, placementId, choreographerEnabled, choreographerFactory]);

	useEffect(() => {
		return installChoreographerDevtoolsBridge({
			setChoreographerEnabled,
		});
	}, [setChoreographerEnabled]);

	return (
		<ChoreographerContext.Provider value={stableChoreographerContext}>
			{children}
		</ChoreographerContext.Provider>
	);
};

export const useChoreographerContext = (): ChoreographerContext => useContext(ChoreographerContext);

export type { ChoreographerProviderProps };
